import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import {
  convertAttendanceRecordToTime,
  convertSqlTimestampToDate,
} from "utils/UtilityFuncs";
import ShipmentDetails from "./ShipmentDetails/ShipmentDetails";
import ShipmentHistoryTab from "./ShipmentHistoryTab";
import { useSearchParams } from "react-router-dom";

export default function ShipmentDetailsLayout() {
  /*   All Hooks
   ********************************************* */
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  /*   All States
   ********************************************* */
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("history");

  /*   All Functions
   ********************************************* */
  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    try {
      setLoading(true);
      const { data } = await API.patch(
        `sales/shipment`,
        {
          id: shipment.id,
          shipmentStatus: newStatus,
        },
        {
          headers: authHeader(),
        }
      );
      if (data.success) {
        dispatch(
          setNotification({
            message: "Shipment updated successfully",
            type: "success",
          })
        );
      } else {
        dispatch(
          setNotification({
            message: data.message,
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // Replace getLatestShipment with generic getShipmentById
  const getShipmentById = async (shipmentId, switchTab = false) => {
    try {
      setLoading(true);
      const { data } = await API.get(`sales/shipment/${shipmentId}`, {
        headers: authHeader(),
      });
      if (data.success) {
        setShipment(data.data);
        if (switchTab) {
          setActiveTab("shipment-details");
        }
      } else {
        dispatch(
          setNotification({
            message: data.message,
            type: "error",
          })
        );
      }
    } catch (error) {
      dispatch(
        setNotification({
          message: error.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const getLatestShipment = async () => {
    try {
      setLoading(true);
      const { data } = await API.get(`sales/shipment/latest`, {
        headers: authHeader(),
      });

      if (data.success) {
        setShipment(data.data);
      }
    } catch (error) {
      dispatch(
        setNotification({
          message: error.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  /*   All UseEffects
   ********************************************* */
  // useEffect(() => {
  //   let timeout = setTimeout(() => {
  //     getLatestShipment();
  //   }, 50);
  //   return () => clearTimeout(timeout);
  // }, []);

  // Handler for selecting shipment from ShipmentsList
  const handleSelectShipment = (shipmentObj) => {
    if (shipmentObj?.shipmentId) {
      getShipmentById(shipmentObj.shipmentId);
    }
  };

  // Add shipment and switch to latest
  const handleAddShipment = async () => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/sales/shipment",
        { shipmentStatus: "pending" },
        { headers: authHeader() }
      );
      if (data.success) {
        if (data.success && data.data) {
          setActiveTab("shipment-details");
          console.log("switched tab to shipment Details");
          setShipment(data.data);
        }
        setLoading(false);
      }
    } catch {
      // Optionally handle error
      dispatch(
        setNotification({
          message: "Error creating shipment",
          type: "error",
        })
      );
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let shipmentId = searchParams.get("shipment-id");
    console.log("Shipment ID from URL:", shipmentId);

    if (shipmentId) {
      getShipmentById(shipmentId, true);
    } else if (!shipment) {
      console.log("running latest shipment");

      getLatestShipment();
    }
  }, [searchParams, shipment]);

  if (!shipment) {
    return (
      <div
        className="px-3 py-4"
        style={{
          marginLeft: 260,
          height: "100vh",
        }}
      >
        {/*  Loading Shipment */}
        <h4>Loading shipment details...</h4>
        <Spinner />
      </div>
    );
  }

  if (shipment && Object.keys(shipment).length === 0) {
    return (
      <div
        className="px-3 py-2"
        style={{
          marginLeft: 260,
          height: "100vh",
        }}
      >
        {loading && <Spinner />}
        {/* Show msg to Create Shipment */}
        <h4>No shipment details available</h4>
        <p>Please create a shipment to view its details.</p>
        <button
          disabled={loading}
          onClick={handleAddShipment}
          className="btn btn-primary"
        >
          Create Shipment
        </button>
      </div>
    );
  }

  return (
    <div
      className="px-3 py-2"
      style={{
        marginLeft: 260,
        height: "100vh",
      }}
    >
      {loading && <Spinner />}
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <h4 className="">
            <span className="d-inline-block me-1">{shipment?.shipmentId}</span>-{" "}
            {convertSqlTimestampToDate(shipment?.createdAt)} -{" "}
            {convertAttendanceRecordToTime(shipment?.createdAt)}
          </h4>
        </div>
        <div className="d-flex align-items-center gap-1">
          <h4>Status:</h4>
          <select
            className="form-control"
            value={shipment?.shipmentStatus}
            onChange={handleStatusChange}
          >
            <option value={"pending"}>Pending</option>
            <option value={"in-transit"}>In-Transit</option>
            <option value={"delivered"}>Delivered</option>
          </select>
        </div>
      </div>

      <div className="row h-100">
        <div className="col-md-12 px-1 pb-5 ">
          <div className="w-100">
            {/* ********************  ************************* */}
            <ul className="nav nav-tabs border-0 d-flex flex-grow-1 my-2 mb-3">
              <li
                onClick={() => {
                  setActiveTab("history");
                }}
                className=""
              >
                <button
                  className={`btn ${
                    activeTab === "history"
                      ? `btn-primary active bg-primary text-white`
                      : "bg-white shadow-sm"
                  }`}
                >
                  <i className="mdi mdi-settings-outline d-md-none d-block"></i>
                  <span className="d-none d-md-block">History</span>
                </button>
              </li>
              <li
                onClick={() => {
                  setActiveTab("shipment-details");
                }}
                className=""
              >
                <button
                  className={`btn ${
                    activeTab === "shipment-details"
                      ? `btn-primary active bg-primary text-white`
                      : "bg-white shadow-sm"
                  }`}
                >
                  <i className="mdi mdi-settings-outline d-md-none d-block"></i>
                  <span className="d-none d-md-block">Shipment Details</span>
                </button>
              </li>

              <li
                onClick={() => {
                  setActiveTab("shipment-Info");
                }}
                className=""
              >
                <button
                  className={`btn ${
                    activeTab === "shipment-Info"
                      ? `btn-primary active bg-primary text-white`
                      : "bg-white shadow-sm"
                  }`}
                >
                  <i className="mdi mdi-settings-outline d-md-none d-block"></i>
                  <span className="d-none d-md-block">Shipment Info</span>
                </button>
              </li>
            </ul>
            {/* Tab Content============================================== */}
            <div
              style={{
                width: "100%",
              }}
            >
              {activeTab === "history" && (
                <ShipmentHistoryTab
                  shipment={shipment}
                  setActiveTab={setActiveTab}
                  setShipment={setShipment}
                  addShipment={handleAddShipment}
                />
              )}
              {activeTab === "shipment-details" && (
                <ShipmentDetails
                  shipment={shipment}
                  onSelectShipment={handleSelectShipment}
                  refreshShipment={getShipmentById}
                />
              )}

              {activeTab === "shipment-Info" && (
                <div className="alert alert-info mt-3">
                  Coming soon. This component will contain info about shipment
                  tracking etc.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
